import React, { useState, useEffect } from 'react';
import { useTranslate } from '@refinedev/core';
import { sentenceCase } from '@scalingworks/refine-react-admin';
import { useGetChallengePlatformsQuery } from '~/generated/hooks';
import { SelectField, Button, TextField } from '@scalingworks/react-admin-ui';

interface ChallengeLinksFormProps {
  value: any[];
  onChange: (links: any[]) => void;
  onDeletedLinksChange?: (deletedLinkIds: string[]) => void;
  initialLinks?: any[]; // Original links for tracking deletions
}

export const ChallengeLinksForm: React.FC<ChallengeLinksFormProps> = ({
  value = [],
  onChange,
  onDeletedLinksChange,
  initialLinks = [],
}) => {
  const t = useTranslate();
  const { data: challengePlatforms } = useGetChallengePlatformsQuery();
  const [deletedLinkIds, setDeletedLinkIds] = useState<string[]>([]);

  // Create platform options with null check
  const platformOptions =
    challengePlatforms?.getChallengePlatforms?.items?.map((platform) => ({
      label: sentenceCase(platform.platformType),
      value: platform.id,
    })) || [];

  // Notify parent about deleted links when they change
  useEffect(() => {
    onDeletedLinksChange?.(deletedLinkIds);
  }, [deletedLinkIds, onDeletedLinksChange]);

  // Ensure we always have at least one link
  useEffect(() => {
    if (value.length === 0) {
      addLink();
    }
  }, [value.length]);

  // Normalize links data - convert platform.id to platformId for form consistency
  useEffect(() => {
    if (value.length > 0) {
      let hasChanges = false;
      const normalizedLinks = value.map((link) => {
        if (link.platform?.id && !link.platformId) {
          hasChanges = true;
          return {
            ...link,
            platformId: link.platform.id,
            platform: undefined, // Remove nested platform object
          };
        }
        return link;
      });

      if (hasChanges) {
        onChange(normalizedLinks);
      }
    }
  }, [value]); // Remove onChange from dependencies to prevent infinite loops

  const addLink = () => {
    const newLinks = [
      ...value,
      {
        id: undefined,
        title: '',
        description: '',
        url: '',
        platformId: '',
      },
    ];
    onChange(newLinks);
  };

  const removeLink = (index: number) => {
    // Prevent removing the last link
    if (value.length <= 1) {
      return;
    }

    const linkToRemove = value[index];

    // If the link has an ID, it's an existing link that needs to be deleted
    if (linkToRemove?.id) {
      setDeletedLinkIds((prev) => [...prev, linkToRemove.id]);
    }

    const newLinks = value.filter((_, i) => i !== index);
    onChange(newLinks);
  };

  const updateLink = (index: number, field: string, fieldValue: string) => {
    const newLinks = [...value];
    newLinks[index] = { ...newLinks[index], [field]: fieldValue };
    onChange(newLinks);
  };

  // Handle platform selection - convert undefined/empty to actual empty string for the form state
  const handlePlatformChange = (index: number, platformId: string | undefined) => {
    updateLink(index, 'platformId', platformId || '');
  };

  // Get platform ID from link data - handle both direct platformId and nested platform.id
  const getLinkPlatformId = (link: any) => {
    if (link.platformId) {
      return link.platformId;
    }
    if (link.platform?.id) {
      return link.platform.id;
    }
    return '';
  };

  // Handle URL field blur to auto-prepend protocol
  const handleUrlBlur = (index: number, url: string) => {
    if (url && !url.match(/^https?:\/\//)) {
      const newLinks = [...value];
      newLinks[index] = { ...newLinks[index], url: `https://${url}` };
      onChange(newLinks);
    }
  };

  return (
    <div className="space-y-4">
      {value.map((link, index) => (
        <div key={index} className="border border-gray-300 rounded-md p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">
              {t('challenges.form.linkNumber', {
                number: index + 1,
                fallback: `Link ${index + 1}`,
              })}
            </h4>
            <Button
              type="button"
              onClick={() => removeLink(index)}
              disabled={value.length <= 1}
              variant={value.length <= 1 ? 'default' : 'danger'}
              size="xs"
              title={
                value.length <= 1
                  ? t('challenges.form.cannotRemoveLastLink', {
                      fallback: 'Cannot remove the last link. At least one link is required.',
                    })
                  : t('common.remove', { fallback: 'Remove' })
              }
            >
              {t('common.remove', { fallback: 'Remove' })}
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <TextField
              label={t('challenges.form.linkTitle', { fallback: 'Title' })}
              value={link.title || ''}
              onValue={(value) => updateLink(index, 'title', value)}
              placeholder={t('challenges.placeholder.linkTitle', {
                fallback: 'Enter link title',
              })}
            />

            <TextField
              label={t('challenges.form.linkDescription', { fallback: 'Description' })}
              type="textarea"
              value={link.description || ''}
              onValue={(value) => updateLink(index, 'description', value)}
              placeholder={t('challenges.placeholder.linkDescription', {
                fallback: 'Enter link description',
              })}
              {...({ rows: 3 } as any)}
            />

            <TextField
              label={t('challenges.form.url', { fallback: 'URL' })}
              type="url"
              value={link.url || ''}
              onValue={(value) => updateLink(index, 'url', value)}
              onBlur={(e) => handleUrlBlur(index, e.target.value)}
              placeholder={t('challenges.placeholder.url', { fallback: 'https://example.com' })}
              required
            />

            <SelectField
              label={t('challenges.form.platform', { fallback: 'Platform' })}
              value={getLinkPlatformId(link) || undefined}
              onValue={(value: string) => handlePlatformChange(index, value)}
              options={platformOptions}
              placeholder={t('challenges.placeholder.platform', { fallback: 'Select platform' })}
              required
            />
          </div>
        </div>
      ))}

      <Button
        type="button"
        onClick={addLink}
        variant="plain"
        block
        className="border-2 border-dashed border-gray-300 hover:border-blue-500 p-4"
        icon={<span className="text-xl">+</span>}
      >
        {t('challenges.form.addLink', { fallback: 'Add Challenge Link' })}
      </Button>
    </div>
  );
};

// Helper function to get platform ID from link data
const getLinkPlatformIdForValidation = (link: any) => {
  if (link.platformId) {
    return link.platformId;
  }
  if (link.platform?.id) {
    return link.platform.id;
  }
  return '';
};

// Export validation function for use in forms
export const validateChallengeLinks = (links: any[]) => {
  if (!links || !Array.isArray(links)) {
    return false;
  }

  const isValidUrl = (url: string) => {
    if (!url || url.trim().length === 0) return false;
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const validLinks = links.filter((link: any) => {
    const hasUrl = isValidUrl(link.url);
    const platformId = getLinkPlatformIdForValidation(link);
    const hasPlatform = platformId && platformId.trim().length > 0;
    return hasUrl && hasPlatform;
  });

  return validLinks.length > 0;
};
